import React from "react";
import { color, radius, space, text, weight, tracking } from "../tokens";

/**
 * <Card> — the workhorse container.
 *
 *   variant  : "default"    → white surface, subtle border, no shadow
 *              "borderless" → for internal grouping inside another Card
 *              "emphasis"   → brand-tinted background
 *   padding  : "sm"  (space.4=16)
 *              "md"  (space.5=20)  default
 *              "lg"  (space.6=24)  comfortable
 *   header   : { title, eyebrow?, right? } — optional header row
 *   as       : element tag override (default "div")
 *
 * Never has shadow. If you're tempted to add shadow, you probably want a modal.
 */
export function Card({
  children,
  variant = "default",
  padding = "md",
  header,
  as: Tag = "div",
  style,
  ...rest
}) {
  const paddingMap = { sm: space[4], md: space[5], lg: space[6] };
  const bg =
    variant === "emphasis" ? color.brand.subtle : color.bg.surface;
  const border =
    variant === "borderless"
      ? "none"
      : `1px solid ${variant === "emphasis" ? color.brand.border : color.border.subtle}`;
  return (
    <Tag
      style={{
        background: bg,
        border,
        borderRadius: radius.lg,
        padding: paddingMap[padding],
        ...style,
      }}
      {...rest}
    >
      {header && <CardHeader {...header} />}
      {children}
    </Tag>
  );
}

export function CardHeader({ title, eyebrow, right }) {
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "flex-start",
        gap: space[3],
        marginBottom: space[4],
      }}
    >
      <div style={{ display: "flex", flexDirection: "column", gap: space[1] }}>
        {eyebrow && (
          <span
            style={{
              fontSize: text.xs,
              fontWeight: weight.semibold,
              color: color.text.tertiary,
              letterSpacing: tracking.wider,
              textTransform: "uppercase",
            }}
          >
            {eyebrow}
          </span>
        )}
        {title && (
          <span
            style={{
              fontSize: text.lg,
              fontWeight: weight.semibold,
              color: color.text.primary,
              letterSpacing: tracking.tight,
            }}
          >
            {title}
          </span>
        )}
      </div>
      {right && <div style={{ flexShrink: 0 }}>{right}</div>}
    </div>
  );
}
